import ExcelJS from 'exceljs';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { getCollaborators } from '@/features/Collaborators/Services/collaboratorPrivateService';
import {
    ALLOCATION_TYPE_METADATA,
    ALLOCATION_DURATION_METADATA,
    ALLOCATION_BUCKET_METADATA,
} from '@/features/Collaborators/Models';
import { RARO_EMAIL_DOMAINS } from '../exportsPage.definitions';
import { downloadWorkbook } from '../utils';

/** Monta o comentário de cabeçalho listando as opções aceitas pelo banco. */
function generateComment(
    metadata: Record<string, { label: string; description: string }>
): string {
    return (
        'Opções Disponíveis (Valor no Banco):\n\n' +
        Object.entries(metadata)
            .map(([key, meta]) => `${key}: ${meta.label} - ${meta.description}`)
            .join('\n')
    );
}

function setHeaderNote(
    worksheet: ExcelJS.Worksheet,
    cell: string,
    metadata: Record<string, { label: string; description: string }>
): void {
    worksheet.getCell(cell).note = {
        texts: [
            {
                font: { size: 10, name: 'Calibri', family: 2 },
                text: generateComment(metadata),
            },
        ],
        margins: { insetmode: 'custom', inset: [0.25, 0.25, 0.35, 0.35] },
    };
}

/**
 * Exporta o cadastro simplificado de colaboradores, com validações e dropdowns
 * prontos para servir de planilha de input de alocações.
 */
export async function exportCollaborators(): Promise<void> {
    const allCollaborators = await getCollaborators({
        orderByField: 'name',
        orderDirection: 'asc',
    });

    const collaborators = allCollaborators.filter((collaborator) => {
        const email = collaborator.email?.toLowerCase() || '';
        return RARO_EMAIL_DOMAINS.some((domain) => email.endsWith(domain));
    });

    const clientsSnapshot = await getDocs(query(collection(db, 'clients'), orderBy('name')));
    const clients = clientsSnapshot.docs.map((doc) => doc.data().name as string);

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Colaboradores');

    worksheet.columns = [
        { header: 'ID', key: 'id', width: 30 },
        { header: 'Status', key: 'status', width: 15 },
        { header: 'Nome', key: 'name', width: 40 },
        { header: 'Tipo de Alocação', key: 'allocationType', width: 30 },
        { header: 'Duração da Alocação', key: 'allocationDuration', width: 30 },
        { header: 'Data de Início', key: 'startDate', width: 20 },
        { header: 'Data de Término', key: 'endDate', width: 20 },
        { header: 'Capacidade Prevista', key: 'bucketType', width: 25 },
        { header: 'Horas Previstas', key: 'bucket', width: 15 },
        { header: 'Taxa', key: 'rate', width: 15 },
        { header: 'Cliente', key: 'clientName', width: 30 },
        { header: 'Responsável Raro', key: 'responsibleName', width: 30 },
        { header: 'Iniciativa', key: 'initiative', width: 30 },
        { header: 'Responsável Cliente', key: 'clientResponsible', width: 30 },
    ];

    collaborators.forEach((collaborator) => {
        worksheet.addRow({
            id: collaborator.id,
            status: collaborator.status,
            name: collaborator.name,
            allocationType: '',
            allocationDuration: '',
            startDate: '',
            endDate: '',
            bucketType: '',
            bucket: '',
            rate: '',
            clientName: '',
            responsibleName: '',
            initiative: '',
            clientResponsible: '',
        });
    });

    // Planilha oculta que alimenta os dropdowns de cliente e responsável
    const dataSheet = workbook.addWorksheet('Dados');
    dataSheet.state = 'hidden';

    dataSheet.getColumn('A').values = ['Clients', ...clients];

    const collaboratorNames = collaborators.map((collaborator) => collaborator.name);
    dataSheet.getColumn('B').values = ['Collaborators', ...collaboratorNames];

    const clientCount = clients.length;
    const collaboratorCount = collaboratorNames.length;

    const allocationValues = Object.keys(ALLOCATION_TYPE_METADATA);
    const durationValues = Object.keys(ALLOCATION_DURATION_METADATA);
    const bucketValues = Object.keys(ALLOCATION_BUCKET_METADATA);

    setHeaderNote(worksheet, 'C1', ALLOCATION_TYPE_METADATA);
    setHeaderNote(worksheet, 'D1', ALLOCATION_DURATION_METADATA);
    setHeaderNote(worksheet, 'G1', ALLOCATION_BUCKET_METADATA);

    const rowCount = collaborators.length;
    for (let i = 2; i <= rowCount + 1; i++) {
        worksheet.getCell(`C${i}`).dataValidation = {
            type: 'list',
            allowBlank: true,
            formulae: [`"${allocationValues.join(',')}"`],
            showErrorMessage: true,
            errorStyle: 'error',
            errorTitle: 'Inválido',
            error: 'Selecione um tipo válido.',
        };

        worksheet.getCell(`D${i}`).dataValidation = {
            type: 'list',
            allowBlank: true,
            formulae: [`"${durationValues.join(',')}"`],
            showErrorMessage: true,
            errorStyle: 'error',
            errorTitle: 'Inválido',
            error: 'Selecione uma duração válida.',
        };

        worksheet.getCell(`E${i}`).dataValidation = {
            type: 'date',
            operator: 'greaterThanOrEqual',
            allowBlank: true,
            formulae: [new Date(1900, 0, 1)],
            showErrorMessage: true,
            errorStyle: 'error',
            errorTitle: 'Data Inválida',
            error: 'Data inválida.',
        };

        worksheet.getCell(`F${i}`).dataValidation = {
            type: 'date',
            operator: 'greaterThanOrEqual',
            allowBlank: true,
            formulae: [new Date(1900, 0, 1)],
            showErrorMessage: true,
            errorStyle: 'error',
            errorTitle: 'Data Inválida',
            error: 'Data inválida.',
        };

        worksheet.getCell(`G${i}`).dataValidation = {
            type: 'list',
            allowBlank: true,
            formulae: [`"${bucketValues.join(',')}"`],
            showErrorMessage: true,
            errorStyle: 'error',
            errorTitle: 'Inválido',
            error: 'Selecione uma capacidade válida.',
        };

        worksheet.getCell(`H${i}`).dataValidation = {
            type: 'decimal',
            operator: 'greaterThanOrEqual',
            allowBlank: true,
            formulae: [0],
            showErrorMessage: true,
            error: 'Valor deve ser positivo.',
        };

        worksheet.getCell(`I${i}`).dataValidation = {
            type: 'decimal',
            operator: 'greaterThanOrEqual',
            allowBlank: true,
            formulae: [0],
            showErrorMessage: true,
            error: 'Valor deve ser positivo.',
        };

        if (clientCount > 0) {
            worksheet.getCell(`J${i}`).dataValidation = {
                type: 'list',
                allowBlank: true,
                formulae: [`Dados!$A$2:$A$${clientCount + 1}`],
                showErrorMessage: true,
                error: 'Selecione um cliente válido.',
            };
        }

        if (collaboratorCount > 0) {
            worksheet.getCell(`K${i}`).dataValidation = {
                type: 'list',
                allowBlank: true,
                formulae: [`Dados!$B$2:$B$${collaboratorCount + 1}`],
                showErrorMessage: true,
                error: 'Selecione um colaborador válido.',
            };
        }
    }

    await downloadWorkbook(workbook, 'colaboradores.xlsx');
}
